import { getKnowledgeList } from '@/api/pc/knowledge'
import { getAssetFolderList } from '@/api/attachmentAsset'

const PROJECT_PAGE_SIZE = 20
const ASSET_PAGE_SIZE = 20
const ROOT_FOLDER_CODE = 'default'

const emptyState = () => ({
  contextKey: '',
  projectList: [],
  projectNextPage: 1,
  projectLastPage: 0,
  projectTotal: 0,
  projectLoading: false,
  projectError: false,
  projectLoaded: false,
  projectRequestId: 0,
  projectRequestPage: 0,
  projectPendingRefresh: false,
  assetFolders: [],
  assetRootFolder: { code: ROOT_FOLDER_CODE, name: '', file_count: 0, isRoot: true },
  assetNextPage: 1,
  assetLastPage: 0,
  assetTotal: 0,
  assetLoading: false,
  assetError: false,
  assetLoaded: false,
  assetRequestId: 0,
  assetRequestPage: 0,
  assetPendingRefresh: false
})

const currentContextKey = rootGetters => {
  const userId = Number(rootGetters['user/user_id']) || 0
  const teamId = Number(rootGetters['user/current_team_id']) || 0
  return `${userId}:${teamId}`
}

const stateContextIsCurrent = (state, rootState) => Boolean(
  rootState.user?.accessToken &&
  state.contextKey === `${Number(rootState.user?.user_id) || 0}:${Number(rootState.user?.current_team_id) || 0}`
)

const hasAuthenticatedContext = rootGetters => Boolean(
  rootGetters['user/accessToken'] &&
  Number(rootGetters['user/user_id']) &&
  Number(rootGetters['user/current_team_id'])
)

const normalizePagination = (pageData, page, pageSize, fallbackTotal = 0) => {
  const total = Number(pageData?.total ?? fallbackTotal) || 0
  const perPage = Number(pageData?.per_page ?? pageSize) || pageSize
  return {
    currentPage: Number(pageData?.current_page ?? page) || page,
    lastPage: Number(pageData?.last_page ?? Math.ceil(total / perPage)) || 0,
    pageSize: perPage,
    total
  }
}

const uniqueRows = (rows, key) => {
  const seen = new Set()
  return rows.filter(item => {
    const value = item?.[key]
    if (value === undefined || value === null || value === '') return true
    const normalized = String(value)
    if (seen.has(normalized)) return false
    seen.add(normalized)
    return true
  })
}

const getters = {
  projectList: (state, getters, rootState) => stateContextIsCurrent(state, rootState) ? state.projectList : [],
  projectNextPage: (state, getters, rootState) => stateContextIsCurrent(state, rootState) ? state.projectNextPage : 1,
  projectLastPage: (state, getters, rootState) => stateContextIsCurrent(state, rootState) ? state.projectLastPage : 0,
  projectTotal: (state, getters, rootState) => stateContextIsCurrent(state, rootState) ? state.projectTotal : 0,
  projectLoading: (state, getters, rootState) => stateContextIsCurrent(state, rootState) && state.projectLoading,
  projectError: (state, getters, rootState) => stateContextIsCurrent(state, rootState) && state.projectError,
  projectLoaded: (state, getters, rootState) => stateContextIsCurrent(state, rootState) && state.projectLoaded,
  projectPagination: (state, getters, rootState) => stateContextIsCurrent(state, rootState)
    ? { total: state.projectTotal, last_page: state.projectLastPage, page: state.projectNextPage, page_size: PROJECT_PAGE_SIZE }
    : { total: 0, last_page: 0, page: 1, page_size: PROJECT_PAGE_SIZE },
  projectHasMore: (state, getters, rootState) => stateContextIsCurrent(state, rootState) && state.projectNextPage <= state.projectLastPage,
  assetFolders: (state, getters, rootState) => stateContextIsCurrent(state, rootState) ? state.assetFolders : [],
  assetRootFolder: (state, getters, rootState) => stateContextIsCurrent(state, rootState)
    ? state.assetRootFolder
    : { code: ROOT_FOLDER_CODE, name: '', file_count: 0, isRoot: true },
  assetNextPage: (state, getters, rootState) => stateContextIsCurrent(state, rootState) ? state.assetNextPage : 1,
  assetLastPage: (state, getters, rootState) => stateContextIsCurrent(state, rootState) ? state.assetLastPage : 0,
  assetTotal: (state, getters, rootState) => stateContextIsCurrent(state, rootState) ? state.assetTotal : 0,
  assetLoading: (state, getters, rootState) => stateContextIsCurrent(state, rootState) && state.assetLoading,
  assetError: (state, getters, rootState) => stateContextIsCurrent(state, rootState) && state.assetError,
  assetLoaded: (state, getters, rootState) => stateContextIsCurrent(state, rootState) && state.assetLoaded,
  assetPagination: (state, getters, rootState) => stateContextIsCurrent(state, rootState)
    ? { currentPage: state.assetNextPage - 1, lastPage: state.assetLastPage, pageSize: ASSET_PAGE_SIZE, total: state.assetTotal }
    : { currentPage: 0, lastPage: 0, pageSize: ASSET_PAGE_SIZE, total: 0 },
  assetHasMore: (state, getters, rootState) => stateContextIsCurrent(state, rootState) && state.assetNextPage <= state.assetLastPage
}

const mutations = {
  resetForContext(state, contextKey) {
    const projectRequestId = state.projectRequestId + 1
    const assetRequestId = state.assetRequestId + 1
    Object.assign(state, emptyState(), { contextKey, projectRequestId, assetRequestId })
  },
  reset(state) {
    const projectRequestId = state.projectRequestId + 1
    const assetRequestId = state.assetRequestId + 1
    Object.assign(state, emptyState(), { projectRequestId, assetRequestId })
  },
  startProjectRequest(state, { requestId, page }) {
    state.projectRequestId = requestId
    state.projectRequestPage = page
    state.projectLoading = true
    state.projectError = false
  },
  setProjectResult(state, { page, rows, total, lastPage }) {
    const combined = page === 1 ? rows : state.projectList.concat(rows)
    state.projectList = uniqueRows(combined, 'id')
    state.projectNextPage = page + 1
    state.projectTotal = total
    state.projectLastPage = lastPage
    state.projectLoaded = true
  },
  setProjectError(state, value) {
    state.projectError = value
  },
  setProjectLoading(state, value) {
    state.projectLoading = value
  },
  setProjectPendingRefresh(state, value) {
    state.projectPendingRefresh = value
  },
  removeProject(state, id) {
    const originalLength = state.projectList.length
    state.projectList = state.projectList.filter(item => String(item?.id) !== String(id))
    if (state.projectList.length !== originalLength) state.projectTotal = Math.max(0, state.projectTotal - 1)
  },
  startAssetRequest(state, { requestId, page }) {
    state.assetRequestId = requestId
    state.assetRequestPage = page
    state.assetLoading = true
    state.assetError = false
  },
  setAssetResult(state, { page, rows, pagination, rootFolder }) {
    const combined = page === 1 ? rows : state.assetFolders.concat(rows)
    state.assetFolders = uniqueRows(combined, 'code')
    state.assetNextPage = page + 1
    state.assetLastPage = pagination.lastPage
    state.assetTotal = pagination.total
    state.assetLoaded = true
    if (rootFolder) state.assetRootFolder = { ...state.assetRootFolder, ...rootFolder, code: ROOT_FOLDER_CODE, isRoot: true }
  },
  setAssetError(state, value) {
    state.assetError = value
  },
  setAssetLoading(state, value) {
    state.assetLoading = value
  },
  setAssetPendingRefresh(state, value) {
    state.assetPendingRefresh = value
  },
  setAssetRootFolder(state, rootFolder) {
    state.assetRootFolder = { ...state.assetRootFolder, ...(rootFolder || {}), code: ROOT_FOLDER_CODE, isRoot: true }
  },
  setAssetRootFileCount(state, count) {
    state.assetRootFolder = { ...state.assetRootFolder, file_count: Number(count) || 0, code: ROOT_FOLDER_CODE, isRoot: true }
  }
}

const actions = {
  reset({ commit }) {
    commit('reset')
  },
  async removeProject({ commit, dispatch, rootGetters }, { id, contextKey }) {
    if (contextKey !== currentContextKey(rootGetters) || !hasAuthenticatedContext(rootGetters)) return false
    commit('removeProject', id)
    return dispatch('loadProjects', { page: 1, force: true })
  },
  setAssetRootFolder({ state, commit, rootGetters }, { rootFolder, contextKey }) {
    if (contextKey !== currentContextKey(rootGetters) || !hasAuthenticatedContext(rootGetters)) return false
    if (state.contextKey !== contextKey) commit('resetForContext', contextKey)
    commit('setAssetRootFolder', rootFolder)
    return true
  },
  setAssetRootFileCount({ state, commit, rootGetters }, { count, contextKey }) {
    if (contextKey !== currentContextKey(rootGetters) || !hasAuthenticatedContext(rootGetters)) return false
    if (state.contextKey !== contextKey) commit('resetForContext', contextKey)
    commit('setAssetRootFileCount', count)
    return true
  },
  async loadProjects({ state, commit, dispatch, rootGetters }, options = {}) {
    if (!hasAuthenticatedContext(rootGetters)) return false
    const contextKey = currentContextKey(rootGetters)
    if (state.contextKey !== contextKey) commit('resetForContext', contextKey)

    const page = Math.max(1, Number(options.page) || state.projectNextPage)
    const force = options.force === true
    if (state.projectLoading) {
      if (page === 1 && force) commit('setProjectPendingRefresh', true)
      return false
    }
    if (page === 1 && state.projectLoaded && !force) return true
    if (page > 1 && (!state.projectLoaded || page > state.projectLastPage)) return false

    const requestId = state.projectRequestId + 1
    commit('startProjectRequest', { requestId, page })
    try {
      const response = await getKnowledgeList({ page, page_size: PROJECT_PAGE_SIZE, keyword: '' })
      if (requestId !== state.projectRequestId || contextKey !== currentContextKey(rootGetters) || !hasAuthenticatedContext(rootGetters)) return false
      if (response?.code !== 0 || !Array.isArray(response.data?.data)) throw new Error('Invalid project list response')
      const total = Number(response.data.total) || 0
      const lastPage = Number(response.data.last_page) || Math.ceil(total / PROJECT_PAGE_SIZE)
      commit('setProjectResult', { page, rows: response.data.data, total, lastPage })
      return true
    } catch (error) {
      if (requestId === state.projectRequestId && contextKey === currentContextKey(rootGetters) && hasAuthenticatedContext(rootGetters)) {
        commit('setProjectError', true)
      }
      return false
    } finally {
      if (requestId === state.projectRequestId) {
        commit('setProjectLoading', false)
        if (state.projectPendingRefresh && contextKey === currentContextKey(rootGetters)) {
          commit('setProjectPendingRefresh', false)
          dispatch('loadProjects', { page: 1, force: true })
        }
      }
    }
  },
  async loadAssetFolders({ state, commit, dispatch, rootGetters }, options = {}) {
    if (!hasAuthenticatedContext(rootGetters)) return false
    const contextKey = currentContextKey(rootGetters)
    if (state.contextKey !== contextKey) commit('resetForContext', contextKey)

    const reset = options.reset === true
    const page = reset ? 1 : Math.max(1, Number(options.page) || state.assetNextPage)
    const force = options.force === true || reset
    if (state.assetLoading) {
      if (page === 1 && force) commit('setAssetPendingRefresh', true)
      return false
    }
    if (page === 1 && state.assetLoaded && !force) return true
    if (page > 1 && (!state.assetLoaded || page > state.assetLastPage)) return false

    const requestId = state.assetRequestId + 1
    commit('startAssetRequest', { requestId, page })
    try {
      const response = await getAssetFolderList(
        { page, page_size: ASSET_PAGE_SIZE },
        { suppressBusinessErrorMessage: true, suppressErrorMessage: true }
      )
      if (requestId !== state.assetRequestId || contextKey !== currentContextKey(rootGetters) || !hasAuthenticatedContext(rootGetters)) return false
      if (![0, 200].includes(Number(response?.code))) throw new Error('Invalid asset folder response')
      const pageData = response.data?.folders || {}
      const rows = Array.isArray(pageData.data) ? pageData.data : []
      const pagination = normalizePagination(pageData, page, ASSET_PAGE_SIZE, rows.length)
      commit('setAssetResult', { page, rows, pagination, rootFolder: response.data?.root_folder })
      return true
    } catch (error) {
      if (requestId === state.assetRequestId && contextKey === currentContextKey(rootGetters) && hasAuthenticatedContext(rootGetters)) {
        commit('setAssetError', true)
      }
      return false
    } finally {
      if (requestId === state.assetRequestId) {
        commit('setAssetLoading', false)
        if (state.assetPendingRefresh && contextKey === currentContextKey(rootGetters)) {
          commit('setAssetPendingRefresh', false)
          dispatch('loadAssetFolders', { page: 1, force: true })
        }
      }
    }
  }
}

export default { namespaced: true, state: emptyState, getters, mutations, actions }
